import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import {
    ContourAssignment,
    ContourModelChoice,
    replanningRunRegistrationsState,
    replanningStageState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { Image3D } from '~/domain/patient/image3d';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useSelectStep } from '~/routes/workflowStepParam';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';

import { deformationDisplayRangeMmState } from '~/state/atoms/deformationDisplayAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { Modality } from '~/domain/dicom/modality';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import { doseFrameOfReferenceUid } from '~/domain/dose/doseFrame';
import { useComparisonContouring } from '~/hooks/workflows/backend/tasks/useComparisonContouring';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useRegistrationBackend, useSessionID } from '../useBackend';
import {
    useActivateRegistration,
    useSelectDeformation,
} from '~/hooks/registration/useRegistrationStore';
import { labelResultRegistrations } from '~/hooks/workflows/labelResultRegistrations';
import { markResampledResultImages } from '~/hooks/workflows/markResampledResultImages';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { isSessionDerivedStructureSet } from './sessionDerivedStructureSet';

export type BackendContourPropagationTaskInput = {
    selectedModel: PredictionModel;
    targetImageSeriesUid?: string;
    selectedStructures?: string[];
    customizationName?: string;
    initRigidSopInstanceUid?: string;
    // The dose resampled with the structures; none when absent.
    doseSopInstanceUid?: string;
    // Set only by a replanning run: Contour+ also contours the target, and these structures come from it.
    contourRois?: readonly ContourAssignment[];
    contourModel?: ContourModelChoice;
};

/**
 * A React hook for registering and propagating structures between images based on the selected model.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useRegistration() {
    const selectStep = useSelectStep();
    const { workingStep } = useWorkflowSteps();
    const primaryImage = useSelectedPrimaryImage();

    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const session = useSessionOrNull();
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const patient = useSelectedPatient();
    const registrationClient = useRegistrationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizer, anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();
    const selectDeformation = useSelectDeformation();
    const activateRegistration = useActivateRegistration();
    const setAdaptDvfDisplayRange = useSetRecoilState(
        deformationDisplayRangeMmState
    );
    const setReplanningStage = useSetRecoilState(replanningStageState);
    const setReplanningRunRegistrations = useSetRecoilState(
        replanningRunRegistrationsState
    );
    const setReplanningRunOutputs = useSetRecoilState(
        replanningRunOutputsState
    );
    const { comparisonModel } = useRecoilValue(contourPropagationTaskState);
    const comparisonContouring = useComparisonContouring(comparisonModel);

    // Each scan on its own session; QA compares the two even when no structure is taken from Contour+.
    const startComparisonContouring = async (
        image: Image3D
    ): Promise<string | undefined> => {
        if (image.firstSlice.modality !== Modality.CT) {
            return undefined;
        }
        try {
            return await comparisonContouring.start(image);
        } catch (error) {
            handleError(error, 'Failed to start Contour+.');
            return undefined;
        }
    };

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // Cleared up front so no failure path leaves the previous run's deformation drawn.
            selectDeformation(undefined);
            setAdaptDvfDisplayRange(null);

            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    // A run emits at most one deformable registration; more cannot all be this run's.
                    const { deformableRegistrations } = processedItems;
                    if (deformableRegistrations.length > 1) {
                        console.warn(
                            `Ignoring ${deformableRegistrations.length} deformable ` +
                                'registrations: cannot tell which belongs to this result.'
                        );
                    }
                    updateJob(job.id, { isImported: true });
                    const resultStructureSet = processedItems.structureSets[0];
                    const targetRegistration = processedItems.registrations[0];
                    const targetImage = resultStructureSet?.image;
                    markResampledResultImages(processedItems);
                    labelResultRegistrations(processedItems);
                    if (!resultStructureSet || !targetImage) {
                        // No identifiers: a UID in a console is PHI.
                        console.warn(
                            'Registration result not presented:',
                            resultStructureSet
                                ? 'the structure set names no image'
                                : 'the run returned no structure set'
                        );
                    } else {
                        if (!targetRegistration) {
                            console.warn(
                                'Registration result carried no rigid registration; ' +
                                    'showing the propagated structures without a comparison.'
                            );
                        }
                        // A new result opens on the stage that judges it, never a later one.
                        setReplanningStage('register');
                        setReplanningRunRegistrations(
                            [
                                ...processedItems.registrations,
                                ...deformableRegistrations,
                            ].map((registration) => registration.sopInstanceUid)
                        );
                        // One dose or none: geometry cannot tell which of two belongs to this run.
                        if (processedItems.doses.length > 1) {
                            console.warn(
                                `Ignoring ${processedItems.doses.length} doses: ` +
                                    'cannot tell which belongs to this result.'
                            );
                        }
                        const carriedDose =
                            processedItems.doses.length === 1
                                ? processedItems.doses[0]
                                : undefined;
                        // Pending until the plan's segmented structures are merged in; never reviewed half-built.
                        setReplanningRunOutputs((outputs) => ({
                            ...outputs,
                            pendingPropagatedUid: resultStructureSet.dicomUid,
                            propagatedUid: undefined,
                            propagatedDoseUid: carriedDose?.dicomUid,
                        }));
                        await initializeEnvironment({
                            structureSets: [resultStructureSet],
                            images: [targetImage],
                            registrations: targetRegistration
                                ? [targetRegistration]
                                : [],
                            doses: carriedDose ? [carriedDose] : [],
                            patients: [],
                            interpolatedImages: [],
                            skippedSeries: [],
                            deformableRegistrations: [],
                        });

                        // Last: the steps above select registrations of their own, including an unheld identity.
                        const deformable =
                            deformableRegistrations.length === 1
                                ? deformableRegistrations[0]
                                : undefined;
                        const resultRegistration =
                            deformable ?? targetRegistration;
                        const counterpart =
                            resultRegistration &&
                            patient?.findCounterpartImage(
                                resultRegistration,
                                targetImage
                            );
                        if (resultRegistration && counterpart) {
                            activateRegistration(
                                resultRegistration.sopInstanceUid,
                                counterpart.seriesUid
                            );
                        } else if (resultRegistration) {
                            console.warn(
                                'Registration result not selected: the scan it registers to is not loaded.'
                            );
                        }
                    }
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (
        input: BackendContourPropagationTaskInput
    ) => {
        if (isSubmitting) {
            return;
        }

        const blobsToUpload: Blob[] = [];
        // Resolved at Start, so an image loaded since the last render is found.
        const registeredImage = patient?.images.find(
            (image) =>
                image !== primaryImage &&
                image.seriesUid === input.targetImageSeriesUid
        );
        if (
            !primaryImage ||
            !registeredImage ||
            !input.selectedModel ||
            !currentStructureSet
        ) {
            const message = 'Missing required data for registration';
            handleError(new Error(message), message);
            return;
        }

        setIsSubmitting(true);
        // Captured before the run: afterwards the propagated set is the current one.
        const sourceUid = currentStructureSet.dicomUid;
        try {
            resetSessionID();
            const taskUID = `${sessionID}.${v4().split('-')[0]}`;

            const primaryImageBlobs = Array.from(
                primaryImage.getSlicesAsDicomBlobs()
            );
            const targetImageBlobs = Array.from(
                registeredImage.getSlicesAsDicomBlobs()
            );
            // Carried only when asked for: the run resamples any dose it is given onto other anatomy.
            const carriedDose = input.doseSopInstanceUid
                ? patient?.doses.find(
                      (dose) => dose.sopInstanceUid === input.doseSopInstanceUid
                  )
                : undefined;
            // The last gate before upload: the run moves the dose as if it were planned on the source scan.
            if (
                input.doseSopInstanceUid &&
                (!carriedDose ||
                    isDerivedDose(carriedDose) ||
                    doseFrameOfReferenceUid(carriedDose) !==
                        primaryImage.frameOfReferenceUid)
            ) {
                handleError(
                    'The dose chosen to carry is not a planned dose on the source scan.'
                );
                return;
            }
            const doseBlobs = carriedDose
                ? [carriedDose.dicomDose.getAsDicomBlob()]
                : [];
            const chosenInitRigid = input.initRigidSopInstanceUid
                ? patient?.getRegistrationWithSopInstanceUid(
                      input.initRigidSopInstanceUid
                  )
                : undefined;
            // Only a matrix can initialize a run; the worker refuses a deformable one.
            const initRigid =
                chosenInitRigid instanceof RigidRegistration
                    ? chosenInitRigid
                    : undefined;
            if (input.initRigidSopInstanceUid && !initRigid) {
                handleError(
                    'The rigid registration chosen to initialize the run is no longer available.'
                );
                return;
            }
            // Synced first: the matrix is edited in memory and the blob is read from the dataset.
            const initRigidBlobs = initRigid
                ? [
                      (initRigid.syncTransformationToDataset(),
                      initRigid.dicomParent.getAsDicomBlob()),
                  ]
                : [];
            const totalSlices =
                primaryImageBlobs.length +
                targetImageBlobs.length +
                1 + // adds structure set slice
                doseBlobs.length +
                initRigidBlobs.length;

            const modelIdentifier = input.customizationName
                ? `${input.selectedModel.name}.${input.customizationName}`
                : input.selectedModel.name;

            const backendParameters: RequestParameters = {
                action: modelIdentifier,
                movingSeriesUID: primaryImage?.seriesUid || '',
                fixedSeriesUID: input.targetImageSeriesUid || '',
                nExpectedFiles: totalSlices.toString(),
                taskUID,
                sessionId: sessionID,
            };

            const primaryBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, primaryImageBlobs)
                : primaryImageBlobs;
            const targetBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, targetImageBlobs)
                : targetImageBlobs;

            if (anonymizer.UIDMapping) {
                if (
                    backendParameters.movingSeriesUID &&
                    backendParameters.movingSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.movingSeriesUID =
                        anonymizer.UIDMapping[
                            backendParameters.movingSeriesUID
                        ];
                }

                if (
                    backendParameters.fixedSeriesUID &&
                    backendParameters.fixedSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.fixedSeriesUID =
                        anonymizer.UIDMapping[backendParameters.fixedSeriesUID];
                }
            }

            if (primaryBlobsToUpload) {
                blobsToUpload.push(...primaryBlobsToUpload);
            } else {
                handleError('Anonymization failed for primary image files.');
                return;
            }

            if (targetBlobsToUpload) {
                blobsToUpload.push(...targetBlobsToUpload);
            } else {
                handleError('Anonymization failed for target image files.');
                return;
            }

            if (currentStructureSet) {
                if (
                    !ensureStructureSetsCurrent(
                        getDatasetEnvironmentRegistry(),
                        [currentStructureSet],
                        'Registration'
                    )
                ) {
                    return;
                }
                if (!session) {
                    throw new Error(
                        'No active patient session for registration.'
                    );
                }
                if (
                    isSessionDerivedStructureSet(
                        session,
                        currentStructureSet.dicomUid
                    )
                ) {
                    handleError(
                        'The current structure set was computed for display only and cannot be sent for registration.'
                    );
                    return;
                }
                const selectedRoiNumbers = currentStructureSet.roiArray
                    .filter((roi) =>
                        input.selectedStructures?.includes(roi.name)
                    )
                    .map((roi) => roi.roiNumber);
                const structureSetBlob =
                    session.structureSetExportService.exportBlob(
                        currentStructureSet.dicomUid,
                        { selectedRoiNumbers }
                    );
                const structureSetBlobToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, [structureSetBlob])
                    : [structureSetBlob];
                if (structureSetBlobToUpload) {
                    blobsToUpload.push(...structureSetBlobToUpload);
                } else {
                    handleError('Anonymization failed for structure set.');
                    return;
                }
            }

            if (doseBlobs.length > 0) {
                const doseBlobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, doseBlobs)
                    : doseBlobs;
                if (doseBlobsToUpload) {
                    blobsToUpload.push(...doseBlobsToUpload);
                } else {
                    // Abort rather than send it unmasked: a dose carries the plan's identifiers.
                    handleError('Anonymization failed for the dose.');
                    return;
                }
            }

            if (initRigidBlobs.length > 0) {
                const initRigidToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, initRigidBlobs)
                    : initRigidBlobs;
                if (initRigidToUpload) {
                    blobsToUpload.push(...initRigidToUpload);
                } else {
                    // Abort rather than send it unmasked: it carries both series' identifiers.
                    handleError(
                        'Anonymization failed for the rigid registration.'
                    );
                    return;
                }
            }

            const newJobDetails: NewJobData = {
                id: sessionID,
                name: 'Adapt+',
                totalItems: blobsToUpload.length,
                type: WorkflowTypes.ADAPT,
                client: registrationClient,
                onLoad: handleProcessedFiles,
                payload: {
                    filesToUpload: blobsToUpload.map((file) => ({ file })),
                    backendParameters,
                    pseudonymized: pseudonymize,
                },
                task: {
                    model: input.selectedModel,
                },
                inputApprovalStatus: currentStructureSet.approvalStatus,
            };

            addJob(newJobDetails);
            setReplanningRunOutputs({
                sourceUid,
                planningSeriesUid: primaryImage.seriesUid,
                targetSeriesUid: registeredImage.seriesUid,
                propagatedRoiNames: input.selectedStructures ?? [],
                contourRois: input.contourRois ?? [],
                contourModel: input.contourModel,
            });
            setReplanningRunRegistrations([]);
            // Whenever a model is chosen, so QA can compare its full output on both scans, whatever the ROI list adopts.
            if (input.contourModel) {
                const [contourJobId, contourSourceJobId] = await Promise.all([
                    startComparisonContouring(registeredImage),
                    startComparisonContouring(primaryImage),
                ]);
                setReplanningRunOutputs((outputs) =>
                    outputs.sourceUid === sourceUid
                        ? { ...outputs, contourJobId, contourSourceJobId }
                        : outputs
                );
            }
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
            if (workingStep.id !== 'registration') {
                selectStep('registration');
            }
        } catch (error) {
            handleError(error, 'Failed to start registration task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startProcessing, isSubmitting };
}
